import React, {useEffect, useRef, useState} from 'react';
import {Autocomplete} from '@material-ui/lab';
import {mapMedicinalProductForAutocomplete} from '../../../utils/prescription';
import {CircularProgress, TextField, useTheme} from '@material-ui/core';
import {MedicinalProductDto} from '../../../types/prescription';
import prescriptionsApi from '../../../api/prescriptions/prescriptionsApi';
import {debounce} from 'lodash-es';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import { referralApi } from '../../../api/referral/referralApi';
import { Icd10Code } from '../../../types/referrals';
import { getPreparedLang } from '../../../utils/lang';

const DEBOUNCE_TIME = 1000;
interface Icd10CodeAutocompleteProps {
    label: string;
    onChange: (newValue: string | Icd10Code) => void;
    icd10Code: Icd10Code | null;
    error?: boolean;
    helperText?: React.ReactNode;
}

const Icd10CodeAutocomplete = (
    props: Icd10CodeAutocompleteProps,
) => {
    const {label, onChange, icd10Code, error, helperText} = props;
    const [Icd10CodeInputValue, setIcd10CodeInputValue] =
        useState<string>('');
    const [icd10CodeLoading, setIcd10CodeLoading] = useState<boolean>(false);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [icd10Codes, setIcd10Codes] = useState<
        Icd10Code[]
    >([]);
    const icd10CodeInputRef = useRef(null);
    const {t, i18n} = useTranslation(i18nNamespaces.REFERRAL);
    const isEnglish = getPreparedLang(i18n?.language) === 'en';

    const fetchIcd10CodesList = async (searchValue: string) => {
        try {
            const {data} = await referralApi.getIcd10Codes(
                searchValue,
            );
            if(data.isSuccess)
                setIcd10Codes(data.result);
        } catch (error) {
            console.error(error);
        } finally {
            setIcd10CodeLoading(false);
        }
    };

    const debouncedSearch = useRef(
        debounce(async (search: string) => {
            try {
                setIcd10CodeLoading(true);
                setIsTyping(false);
                await fetchIcd10CodesList(search);
            } finally {
                setIcd10CodeLoading(false);
            }
        }, DEBOUNCE_TIME),
    ).current;

    useEffect(() => {
        debouncedSearch(Icd10CodeInputValue);
    }, [Icd10CodeInputValue]);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);


    const handleChangeInput = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setIcd10CodeInputValue(event.target.value);
        setIsTyping(true);
        debouncedSearch(event.target.value);
    };

    const renderOptionLabel = (option: Icd10Code) => {
            if(!option) return null;
            const name = isEnglish ? option.nameEn : option.namePl;
            return `${option.code} - ${name}`;
        }

    return (
        <Autocomplete
            loading={icd10CodeLoading}
            options={icd10Codes}
            getOptionSelected={option => option.code === icd10Code?.code}
            getOptionLabel={renderOptionLabel}
            noOptionsText={t('noResults')}
            value={icd10Code}
            onChange={(_, newValue) => onChange(newValue)}
            renderInput={params => (
                <TextField
                    {...params}
                    inputRef={icd10CodeInputRef}
                    onInput={handleChangeInput}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {(isTyping || icd10CodeLoading) ? (
                                    <CircularProgress
                                        color="secondary"
                                        size={20}
                                        style={{marginRight: 10}}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}

export default Icd10CodeAutocomplete;
